import React, { useRef, useState } from "react";
import { Input, Select, InputNumber, Tag, Radio } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { UploadCloud, FileSpreadsheet, Loader2, X, Database } from "lucide-react";
import { SetupState, SPLIT_OPTIONS } from "./mockData";
import { parseFileRows, validateRctFileRows, aggregateToWeeklyRows, buildRctInputFile } from "./rctExperimentApi";
import CustomExcelIcon from "../../../assets/icons/CustomExcelIcon";

const MAX_DURATIONS = 5;
const MIN_DURATION_WEEKS = 4;
const MAX_DURATION_WEEKS = 8;
// .xls/.xlsx are converted to CSV client-side before upload (see
// rctExperimentApi's parseFileRows/buildRctInputFile) - the backend only
// ever receives a CSV.
const ACCEPTED_EXTENSIONS = [".csv", ".xls", ".xlsx"];

interface SetupStepProps {
  setup: SetupState;
  onChange: (patch: Partial<SetupState>) => void;
  readOnly?: boolean;
}

const Card: React.FC<{ children: React.ReactNode; locked?: boolean; className?: string }> = ({
  children,
  locked,
  className = "",
}) => (
  <div
    className={`relative bg-white border border-gray-200 rounded-xl p-6 shadow-sm mb-5 ${
      locked ? "opacity-45 pointer-events-none select-none" : ""
    } ${className}`}
  >
    {locked && (
      <span className="absolute top-4 right-5 text-xs font-semibold text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">
        Upload Data to Unlock
      </span>
    )}
    {children}
  </div>
);

const CardHeader: React.FC<{ title: string; tag?: string }> = ({ title, tag }) => (
  <div className="flex justify-between items-center mb-4">
    <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
    {tag && (
      <span className="text-xs font-semibold uppercase tracking-wide text-primary bg-primary/10 px-2.5 py-1 rounded-full">
        {tag}
      </span>
    )}
  </div>
);

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="flex items-center gap-2 text-base font-semibold text-gray-800 mb-3">
    {children}
  </label>
);

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isAcceptedFile = (file: File) => {
  const name = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
};

const SetupStep: React.FC<SetupStepProps> = ({ setup, onChange, readOnly = false }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [durationInput, setDurationInput] = useState("");
  const [durationError, setDurationError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [dataSourceType, setDataSourceType] = useState<"file" | "database">("file");

  const resetFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileSelected = async (file: File | undefined) => {
    if (!file) return;

    if (!isAcceptedFile(file)) {
      setFileError(`"${file.name}" is not a supported file type. Please upload a .csv, .xls, or .xlsx file.`);
      resetFileInput();
      return;
    }

    setFileError(null);
    onChange({ uploadStatus: "uploading" });

    try {
      const { headers, rows } = await parseFileRows(file);

      // Auto-detected from a sample of the actual data rows - no column-name
      // requirements and no manual mapping step.
      const validation = validateRctFileRows(headers, rows);
      if (!validation.valid) {
        setFileError(validation.reason || `"${file.name}" doesn't look like a valid data file.`);
        onChange({ uploaded: false, file: null, uploadStatus: "idle" });
        resetFileInput();
        return;
      }

      const weeklyRows = aggregateToWeeklyRows(
        rows,
        validation.dateColumn!,
        validation.locationColumn!,
        validation.kpiColumn!
      );
      const canonicalFile = buildRctInputFile(weeklyRows, file.name);
      onChange({ uploaded: true, file: canonicalFile, uploadStatus: "done" });
    } catch (err) {
      setFileError(`Could not read "${file.name}". Please check it's a valid CSV or Excel file.`);
      onChange({ uploaded: false, file: null, uploadStatus: "idle" });
      resetFileInput();
    }
  };

  const handleRemoveFile = () => {
    setFileError(null);
    onChange({ uploaded: false, file: null, uploadStatus: "idle" });
    resetFileInput();
  };

  const addDuration = (raw: string) => {
    const n = parseInt(raw.trim(), 10);
    if (!Number.isInteger(n)) return;
    if (n < MIN_DURATION_WEEKS || n > MAX_DURATION_WEEKS) {
      setDurationError(`Enter a duration between ${MIN_DURATION_WEEKS} and ${MAX_DURATION_WEEKS} weeks.`);
      return;
    }
    setDurationError(null);
    if (setup.durations.includes(n)) return;
    if (setup.durations.length >= MAX_DURATIONS) return;
    onChange({ durations: [...setup.durations, n] });
  };

  const handleDurationKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addDuration(durationInput);
      setDurationInput("");
    } else if (e.key === "Backspace" && durationInput === "" && setup.durations.length > 0) {
      onChange({ durations: setup.durations.slice(0, -1) });
    }
  };

  const removeDuration = (value: number) => {
    onChange({ durations: setup.durations.filter((d) => d !== value) });
  };

  const isUploaded = setup.uploaded;
  const durationsFull = setup.durations.length >= MAX_DURATIONS;

  return (
    <div>
      {/* Header Info Banner */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 shadow-sm border border-blue-100 mb-5">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 mt-1">
            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center shadow-md">
              <CustomExcelIcon className="text-white text-2xl" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Historical Data</h3>
            <div className="space-y-1 text-sm text-gray-600">
              <p className="flex items-center gap-2">
                <CheckCircleOutlined className="text-blue-500" />
                Upload a file containing conversion data before the campaign started
              </p>
              <p className="flex items-center gap-2">
                <CheckCircleOutlined className="text-blue-500" />
                Format: <span className="font-semibold">CSV or Excel (.xlsx/.xls)</span>, with column
                headers in the first row
              </p>
            </div>
          </div>
        </div>
      </div>

      <Card>
        <div>
          <FieldLabel>
            Study Name <span className="text-red-500">*</span>
          </FieldLabel>
          <Input
            size="large"
            placeholder="Enter a name for this study"
            value={setup.studyName}
            onChange={(e) => onChange({ studyName: e.target.value })}
            disabled={readOnly}
          />
        </div>
      </Card>

      <Card>
        <CardHeader title="Data Source" />

        <Radio.Group
          value={dataSourceType}
          onChange={(e) => setDataSourceType(e.target.value)}
          className="w-full"
          disabled={readOnly}
        >
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div
              className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                dataSourceType === "file"
                  ? "border-primary bg-primary/5"
                  : "border-gray-200 bg-white hover:border-primary/40"
              }`}
            >
              <Radio value="file" className="w-full">
                <div className="ml-1">
                  <p className="font-semibold text-gray-800 flex items-center gap-2">
                    <FileSpreadsheet className="w-4 h-4 text-primary" /> Upload File
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    .csv, .xls or .xlsx · DMA-level, daily or weekly rows — daily data is
                    aggregated into weeks automatically
                  </p>
                </div>
              </Radio>
            </div>

            <div className="relative border-2 rounded-xl p-4 border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed">
              <Radio value="database" disabled className="w-full">
                <div className="ml-1">
                  <p className="font-semibold text-gray-500 flex items-center gap-2">
                    <Database className="w-4 h-4 text-gray-400" /> Use Database Data
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Load historical data from your connected database
                  </p>
                </div>
              </Radio>
              <span className="absolute top-3 right-3 text-[10px] font-semibold uppercase tracking-wide text-gray-400 bg-white border border-gray-200 px-2 py-0.5 rounded-full">
                Coming soon
              </span>
            </div>
          </div>
        </Radio.Group>

        {readOnly ? (
          <div className="border border-gray-200 rounded-xl bg-gray-50 px-5 py-4 text-sm text-gray-500">
            Historical data was uploaded when this experiment was created.
          </div>
        ) : (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_EXTENSIONS.join(",")}
              className="hidden"
              onChange={(e) => handleFileSelected(e.target.files?.[0])}
            />

            {!setup.uploaded && setup.uploadStatus !== "uploading" && (
              <>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                    fileError
                      ? "border-red-300 bg-red-50 hover:border-red-400"
                      : "border-gray-200 bg-gray-50 hover:border-primary hover:bg-primary/5"
                  }`}
                >
                  <div className="w-9 h-9 rounded-lg bg-white border border-gray-200 inline-flex items-center justify-center mb-2 text-gray-400">
                    <UploadCloud className="w-[18px] h-[18px]" />
                  </div>
                  <div className="font-semibold text-base text-gray-800">Click to upload weekly KPI data</div>
                </div>
                {fileError && <p className="text-sm text-red-600 mt-2">{fileError}</p>}
              </>
            )}

            {setup.uploadStatus === "uploading" && (
              <div className="flex items-center gap-2.5 text-gray-600 text-sm p-8 justify-center border-2 border-dashed border-gray-200 rounded-xl bg-gray-50">
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
                <span>Uploading file…</span>
              </div>
            )}

            {isUploaded && setup.file && (
              <div className="border border-primary rounded-xl bg-white flex items-center justify-between px-5 py-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                    <FileSpreadsheet className="w-[18px] h-[18px]" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-base text-gray-900 truncate">{setup.file.name}</div>
                    <p className="text-sm text-gray-500 mt-0.5">{formatBytes(setup.file.size)}</p>
                  </div>
                </div>
                <button
                  onClick={handleRemoveFile}
                  className="text-gray-400 hover:text-gray-600 flex-shrink-0 ml-3"
                  title="Remove file"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </Card>

      <Card locked={!isUploaded && !readOnly}>
        <CardHeader title="Randomization Parameters" />
        <div className="grid grid-cols-2 gap-6">
          <div>
            <FieldLabel>
              Candidate Test Durations (weeks) <span className="text-red-500">*</span>
            </FieldLabel>
            <Input
              size="large"
              value={durationInput}
              onChange={(e) => {
                setDurationError(null);
                setDurationInput(e.target.value.replace(/[^\d]/g, ""));
              }}
              onKeyDown={handleDurationKeyDown}
              placeholder={
                durationsFull
                  ? `Maximum ${MAX_DURATIONS} durations reached`
                  : `Type a number of weeks (${MIN_DURATION_WEEKS}-${MAX_DURATION_WEEKS}) and press Enter`
              }
              status={durationError ? "error" : undefined}
              disabled={readOnly || durationsFull}
            />
            {durationError && <p className="text-sm text-red-600 mt-2">{durationError}</p>}
            {setup.durations.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {setup.durations.map((d) => (
                  <Tag
                    key={d}
                    closable={!readOnly}
                    onClose={() => removeDuration(d)}
                    className="!text-sm !py-1 !px-2.5"
                  >
                    {d} wks
                  </Tag>
                ))}
              </div>
            )}
            <p className="text-gray-500 text-sm mt-2">
              Add up to {MAX_DURATIONS} durations, each between {MIN_DURATION_WEEKS} and {MAX_DURATION_WEEKS}{" "}
              weeks. We'll test all of them and use the one that fits best.
            </p>
          </div>
          <div>
            <FieldLabel>
              Candidate Test / Control Splits <span className="text-red-500">*</span>
            </FieldLabel>
            <Select
              className="w-full"
              size="large"
              placeholder="Select a split"
              value={setup.splits[0]}
              onChange={(value: SetupState["splits"][number]) =>
                onChange({ splits: value ? [value] : [] })
              }
              options={SPLIT_OPTIONS.map((s) => ({
                label: s.label,
                value: s.key,
              }))}
              disabled={readOnly}
            />
            <p className="text-gray-500 text-sm mt-2">
              Each study runs one split. To compare splits, create a separate study per split.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-6 mt-5">
          <div>
            <FieldLabel>
              Weekly Incremental Spend Cap <span className="text-red-500">*</span>
            </FieldLabel>
            <InputNumber
              className="w-full"
              prefix={<span className="text-gray-500 font-medium">$</span>}
              size="large"
              min={0}
              step={1000}
              placeholder="e.g. 80,000"
              value={setup.weeklyCap}
              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              onChange={(v) => onChange({ weeklyCap: v })}
              disabled={readOnly}
            />
          </div>
          <div>
            <FieldLabel>
              Total Weekly Business As Usual Ad Spend <span className="text-red-500">*</span>
            </FieldLabel>
            <InputNumber
              className="w-full"
              prefix={<span className="text-gray-500 font-medium">$</span>}
              size="large"
              min={0}
              step={10000}
              placeholder="e.g. 1,000,000"
              value={setup.totalWeeklySpend}
              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              onChange={(v) => onChange({ totalWeeklySpend: v })}
              disabled={readOnly}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

export default SetupStep;
